<template>
  <el-popover
    placement="bottom"
    :width="width"
    popper-class="nebMultiSearchableSelect"
    trigger="click"
    :visible-arrow="false"
    @show="onOpen"
    @hide="onClose"
    transition="el-zoom-in-top"
    :popper-options="popoverOptions">
    <!-- SELECT-BUTTON -->
    <div class="nebMultiSearchableSelect-selected"
      :class="[!someSelected ? 'placeholder': 'selected', { open }, size]"
      :style="selectedStyle"
      slot="reference">
      <span>{{ selectedText }}</span>
      <caret-down-icon class="nebMultiSearchableSelect-selected-openIndicator"
        :class="size"
        height="8px"
        width="10px"
        :rotate="open ? '-180' : '0'"/>
    </div>
    <!-- SELECT-DROPDOWN -->
    <div class="nebMultiSearchableSelect-optionList scrollable">
      <ntv-search-input class="nebMultiSearchableSelect-optionList-search"
        :placeholder="searchPlaceholder"
        @input="onSearchInput"
        ref="searchInput"/>
      <div
        class="nebMultiSearchableSelect-optionList-clearBox"
        v-if="someSelected">
        <span class="nebMultiSearchableSelect-optionList-clear"
          @click="clearSelected">
          {{clearSelectedText}}
        </span>
      </div>
      <div class="nebMultiSearchableSelect-optionList-option"
        :class="[{ 'disabled': opt.disabled, 'inSection': opt.inSection }, size]"
        v-for="(opt, idx) in flatOptionsBeforeDivider"
        :key="`A-${idx}`"
        @click="evt => onOptionSelect(evt, opt)">
        <p class="nebMultiSearchableSelect-optionList-header"
          v-if="opt.section">
          {{ opt.section }}
        </p>
        <neb-checkbox v-else
          :label="opt.label"
          :value="divider || isOptionSelected(opt)"
          :disabled="opt.disabled"/>
      </div>
      <template v-if="divider">
        <div class="nebMultiSearchableSelect-optionList-divider"
          v-if="someSelected && !allSelected"/>
        <div class="nebMultiSearchableSelect-optionList-option"
          :class="[{ 'disabled': opt.disabled, 'inSection': opt.inSection }, size]"
          v-for="(opt, idx) in flatOptionsAfterDivider"
          :key="`B-${idx}`"
          @click="evt => onOptionSelect(evt, opt)">
          <p class="nebMultiSearchableSelect-optionList-header"
            v-if="opt.section">
            {{ opt.section }}
          </p>
          <neb-checkbox v-else
            :label="opt.label"
            :value="false"
            :disabled="opt.disabled"
          />
        </div>
      </template>
    </div>
  </el-popover>
</template>

<script>
import {
  cloneDeep,
  includes,
  isUndefined,
  isNull,
  isEmpty,
  every,
  truncate,
  uniq,
  filter
} from 'lodash'
import CaretDownIcon from '@/components/icons/CaretDown.vue'
import NebCheckbox from '../Checkbox'
import NtvSearchInput from '@nui/SearchInput'
export default {
  name: 'nebMultiSearchableSelect',

  components: {
    CaretDownIcon,
    NebCheckbox,
    NtvSearchInput
  },

  props: {
    /**
     * The value of the select.
     *
     * @type {?(String|Number|String[]|Number[])}
     * @default null
     */
    value: {
      type: [Number, String, Array],
      default: null
    },
    /**
     * An Array of option objects.
     *
     * @type {Object[]}
     * @type {String} Object[].label - The option label.
     * @type {?(String|Number)} Object[].value - The option value.
     * @type {String=} Object[].section - The section this option should fall under. Can be omitted if the option
     * should not fall under a section.
     * @type {Boolean=} disabled - If the option should be disabled.
     * @default []
     */
    options: {
      type: Array[Object],
      default: () => []
    },
    /**
     * The size of the select. For now, only has one option.
     *
     * 'normal'
     *
     * @type {String}
     */
    size: {
      type: String,
      default: 'normal',
      required: false
    },
    /**
     * Placeholder for when no value has been selected.
     *
     * @type {String}
     */
    placeholder: {
      type: String,
      default: 'Please select an option',
      required: false
    },
    /**
     * Prefix for the selected box.
     *
     * @type {String}
     */
    prefix: {
      type: String,
      default: '',
      required: false
    },
    /**
     * Selected options will be placed in a separate section
     * above the list of available options.
     *
     * @type {String}
     */
    divider: {
      type: Boolean,
      default: true,
      required: false
    },
    /**
     * Width of select dropdown
     * @type {Number}
     */
    width: {
      type: String,
      default: '260',
      required: false
    },
    fitToContainer: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    open: false,
    clearSelectedText: 'Clear Selected',
    searchPlaceholder: 'Search',
    search: '',
    popoverOptions: {
      boundariesElement: 'body',
      // The default options create a problem with scroll and
      // turning on gpu acceleration for some reason fixes it
      gpuAcceleration: true
    }
  }),

  methods: {
    onOpen () {
      this.open = true
      this.$emit('open')
      // Use nextTick so that it is possible to get the ref of
      // the search input as there is a transition on the dropdown
      // and we need to wait until that transition is finished.
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focusInput()
      })
    },
    onClose () {
      this.open = false
    },
    onSelectedClick () {
      this.open ? this.onClose() : this.onOpen()
    },
    onSearchInput (val, evt) {
      this.search = val
      this.$emit('search', evt, val)
    },
    clearSelected () {
      this.$emit('input', [])
    },
    isOptionSelectEvent (evt) {
      return [ ...document.querySelectorAll('.nebMultiSearchableSelect-optionList-option') ]
        .some(el => el === evt.target || el.contains(evt.target)
  )
    },
    selectOption (option) {
      const newSelected = cloneDeep(this.selected)
      if (this.selected.indexOf(option.value) === -1) {
        newSelected.push(option.value)
      }
      this.$emit('input', newSelected)
    },
    unselectOption (option) {
      const newSelected = this.selected.filter(opt => opt !== option.value)
      this.$emit('input', newSelected)
    },
    toggleSelected (option) {
      this.isOptionSelected(option) ? this.unselectOption(option) : this.selectOption(option)
    },
    onOptionSelect (evt, option) {
      if (!this.isOptionSelectEvent(evt) || option.disabled) return
      this.toggleSelected(option)
    },
    isOptionSelected (option) {
      return includes(this.selected, option.value)
    },
    exists (thing) {
      return !isUndefined(thing) && !isNull(thing) && !isEmpty(thing)
    },
    sectionHasSomeSelected (section) {
      return this.filteredOptions.some(opt => opt.section === section && this.isOptionSelected(opt))
    },
    sectionHasAllSelected (section) {
      return this.filteredOptions.filter(opt => opt.section === section).every(this.isOptionSelected)
    },
    hasSection (beforeDivider, opt) {
      return beforeDivider
        ? !this.divider || (opt.section && this.sectionHasSomeSelected(opt.section))
        : opt.section && !this.sectionHasAllSelected(opt.section)
    },
    hasOption (beforeDivider, opt) {
      return beforeDivider
        ? !opt.section && (!this.divider || (this.divider && this.isOptionSelected(opt)))
        : !opt.section && !this.isOptionSelected(opt)
    }
  },

  computed: {
    selected () {
      return this.value
    },
    filteredOptions () {
      return this.options.filter(opt => opt.label.toLowerCase().indexOf(this.search.toLowerCase()) > -1)
    },
    someSelected () {
      return this.selected.length !== 0
    },
    allSelected () {
      return every(this.filteredOptions, this.isOptionSelected)
    },
    selectedText () {
      if (this.someSelected) {
        const pre = this.prefix ? `${this.prefix} ` : ''
        const option = this.options.find(opt => opt.value === this.selected[0])
        const elm = this.exists(option) ? truncate(option.label, {'length': 29}) : 'unknown'
        const plus = this.selected.length > 1 ? `, +${this.selected.length - 1}` : ''
        return `${pre}${elm}${plus}`
      }
      return this.prefix ? this.prefix : this.placeholder
    },
    sections () {
      return uniq(this.filteredOptions
        .filter(opt => this.exists(opt.section))
        .map(opt => opt.section))
    },
    flatOptions () {
      return this.sections.reduce((f, s) => {
        const cleanOptions = this.filteredOptions.filter(opt => opt.section === s)
          .map(opt => ({ label: opt.label, value: opt.value, inSection: true }))
        return f.concat([{ section: s }], cleanOptions)
      }, []).concat(this.filteredOptions.filter(opt => !this.exists(opt.section)))
    },
    flatOptionsBeforeDivider () {
      return filter(this.flatOptions, opt => this.hasSection(true, opt) || this.hasOption(true, opt))
    },
    flatOptionsAfterDivider () {
      return filter(this.flatOptions, opt => this.hasSection(false, opt) || this.hasOption(false, opt))
    },
    selectedStyle () {
      if (!this.fitToContainer) {
        return { width: `${this.width}px` }
      } else {
        return {}
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../style.scss';

.nebMultiSearchableSelect {
  position: relative;
  text-align: left;
  outline: none;
  box-sizing: border-box;

  &-selected {
    border-radius: 3px;
    border: 1px solid #a0c3d2;
    padding-left: 15px;
    padding-top: 1px;
    cursor: pointer;
    user-select: none;
    color: #5c5e62;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    outline: none;
    width: 100%;

    &.selected {
      background-color: #f5fbff;
    }

    &.selected:focus {
      outline: none;
    }

    &.open {
      border-color: #74cfe7;
    }

    &.normal {
      font-size: 12px;
      height: 34px;
      line-height: 34px;
    }

    &.placeholder {
      color: $slate-grey;
    }

    &-openIndicator {
      position: absolute;
      right: 10px;

      &.normal {
        top: 12px;
      }
    }
  }

  &-optionList {
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid #cecece;
    left: 0;
    right: 0;
    top: 39px;
    padding-top: 4px;
    background-color: #fff;
    z-index: 999;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

    max-height: 250px;
    overflow-x: hidden;
    overflow-y: scroll;

    &.scrollable {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5), inset 0 -20px 20px -20px rgba(0, 0, 0, 0.5);
    }

    &-search {
      padding: 7px 15px;
    }

    &-clearBox {
      display: block;
      padding-left: 15px;
      margin: 5px 0px;
    }

    &-header {
      margin: 0;
      line-height: 2.5;
      font-weight: bold;
      color: #5c5e62;
    }

    &-clear {
      color: #74cfe7;
      cursor: pointer;
    }

    &-option {
      padding-left: 15px;
      cursor: pointer;
      user-select: none;
      color: #5c5e62;
      padding: 9px 15px;

      &.inSection {
        padding-left: 30px;
      }

      &.normal {
        font-size: 12px;
      }

      &.disabled {
        cursor: not-allowed;
      }

      &:hover {
        background-color: #e8f4fc;
      }
    }

    &-divider {
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      width: 100%;
      margin-top: 10px;
      margin-bottom: 10px;
      height: 1px;
      background-color: #cecece;
    }
  }

}
</style>

<style lang="scss">
.nebMultiSearchableSelect {
  &.el-popper {
    padding: 0;
    background-color: red;
    margin-top: 4px;
  }
}
</style>
